import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  limit,
} from "firebase/firestore";
import { ref, getDownloadURL, listAll, getMetadata } from "firebase/storage";
import { db, storage } from "../firebase";
import { OrderStatus } from "./firebaseUtils";

export interface AdminOrderData {
  id: string;
  clientName: string;
  clientEmail: string;
  submissionDate: string;
  status: OrderStatus;
  productType: string;
  totalAmount: number;
  filesCount: number;
  formStatus?: Record<string, any>;
  statusHistory?: any[];
  lastUpdated?: string;
}

export interface OrderFile {
  name: string;
  url: string;
  size: number;
  type: string;
  uploadDate: string;
}

// Types
export interface OrderWithClient {
  id: string;
  status: string;
  clientEmail: string;
  clientName?: string;
  productType?: string;
  totalAmount?: number;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  adminNotes?: string;
  statusHistory?: OrderStatusUpdate[];
}

export interface OrderStatusUpdate {
  status: string;
  timestamp: Timestamp;
  reason?: string;
  notes?: string;
  adminUserId?: string;
  automated?: boolean;
}

export interface OrderStatistics {
  totalOrders: number;
  pendingOrders: number;
  inProgressOrders: number;
  completedOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersByStatus: Record<string, number>;
}

export interface ClientInfo {
  email: string;
  name?: string;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate?: Timestamp;
  orders: OrderWithClient[];
}

/**
 * Fetch all orders with client information
 */
export async function fetchAllOrders(): Promise<OrderWithClient[]> {
  try {
    const ordersRef = collection(db, "orders");
    const ordersQuery = query(ordersRef, orderBy("createdAt", "desc"));
    const ordersSnapshot = await getDocs(ordersQuery);

    const orders: OrderWithClient[] = [];

    for (const orderDoc of ordersSnapshot.docs) {
      const orderData = orderDoc.data();

      // Get client information
      let clientName = orderData.clientName;
      if (!clientName && orderData.userId) {
        try {
          const userDoc = await getDoc(doc(db, "users", orderData.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            clientName = userData.name || userData.displayName;
          }
        } catch (error) {
          console.warn("Error fetching user data:", error);
        }
      }

      orders.push({
        id: orderDoc.id,
        status: orderData.status || "pending",
        clientEmail: orderData.clientEmail || orderData.email || "Unknown",
        clientName,
        productType: orderData.productType || orderData.type,
        totalAmount: orderData.totalAmount || orderData.amount,
        createdAt: orderData.createdAt || Timestamp.now(),
        updatedAt: orderData.updatedAt,
        adminNotes: orderData.adminNotes,
        statusHistory: orderData.statusHistory || [],
      });
    }

    return orders;
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw new Error("Failed to fetch orders");
  }
}

/**
 * Fetch orders by status
 */
export async function fetchOrdersByStatus(
  status: string
): Promise<OrderWithClient[]> {
  try {
    const ordersRef = collection(db, "orders");
    const ordersQuery = query(
      ordersRef,
      where("status", "==", status),
      orderBy("createdAt", "desc")
    );
    const ordersSnapshot = await getDocs(ordersQuery);

    return ordersSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as OrderWithClient[];
  } catch (error) {
    console.error("Error fetching orders by status:", error);
    throw new Error("Failed to fetch orders by status");
  }
}

/**
 * Get count of files for an order
 */
async function getOrderFilesCount(orderId: string): Promise<number> {
  try {
    const orderFilesRef = ref(storage, `orders/${orderId}`);
    const result = await listAll(orderFilesRef);
    return result.items.length;
  } catch (error) {
    // If folder doesn't exist or no files, return 0
    return 0;
  }
}

/**
 * Get all files for a specific order
 */
export async function getOrderFiles(orderId: string): Promise<string[]> {
  try {
    const orderFolderRef = ref(storage, `orders/${orderId}`);
    const fileList = await listAll(orderFolderRef);

    const fileUrls: string[] = [];
    for (const fileRef of fileList.items) {
      const downloadURL = await getDownloadURL(fileRef);
      fileUrls.push(downloadURL);
    }

    return fileUrls;
  } catch (error) {
    console.error("Error fetching order files:", error);
    return [];
  }
}

/**
 * Download all files for an order (opens in new tabs)
 */
export async function downloadOrderFiles(orderId: string): Promise<void> {
  try {
    const fileUrls = await getOrderFiles(orderId);

    if (fileUrls.length === 0) {
      throw new Error("No files found for this order");
    }

    // Open each file in a new tab for download
    fileUrls.forEach((url) => {
      window.open(url, "_blank");
    });
  } catch (error) {
    console.error("Error downloading order files:", error);
    throw error;
  }
}

/**
 * Get order statistics for dashboard
 */
export async function getOrderStatistics(): Promise<OrderStatistics> {
  try {
    const orders = await fetchAllOrders();

    const stats: OrderStatistics = {
      totalOrders: orders.length,
      pendingOrders: 0,
      inProgressOrders: 0,
      completedOrders: 0,
      totalRevenue: 0,
      averageOrderValue: 0,
      ordersByStatus: {},
    };

    orders.forEach((order) => {
      // Count by status
      stats.ordersByStatus[order.status] =
        (stats.ordersByStatus[order.status] || 0) + 1;

      // Specific status counts
      switch (order.status) {
        case "pending":
          stats.pendingOrders++;
          break;
        case "in-progress":
          stats.inProgressOrders++;
          break;
        case "completed":
          stats.completedOrders++;
          break;
      }

      // Revenue calculations
      if (order.totalAmount) {
        stats.totalRevenue += order.totalAmount;
      }
    });

    stats.averageOrderValue =
      stats.totalOrders > 0 ? stats.totalRevenue / stats.totalOrders : 0;

    return stats;
  } catch (error) {
    console.error("Error calculating order statistics:", error);
    throw new Error("Failed to calculate order statistics");
  }
}

/**
 * Update order status with admin notes
 */
export async function updateOrderStatusAdmin(
  orderId: string,
  newStatus: string,
  updateInfo: {
    reason?: string;
    notes?: string;
    adminUserId?: string;
  }
): Promise<Partial<OrderWithClient>> {
  try {
    const orderRef = doc(db, "orders", orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      throw new Error("Order not found");
    }

    const currentData = orderDoc.data();
    const statusUpdate: OrderStatusUpdate = {
      status: newStatus,
      timestamp: Timestamp.now(),
      reason: updateInfo.reason,
      notes: updateInfo.notes,
      adminUserId: updateInfo.adminUserId,
      automated: false,
    };

    const updateData = {
      status: newStatus,
      updatedAt: Timestamp.now(),
      adminNotes: updateInfo.notes,
      statusHistory: [...(currentData.statusHistory || []), statusUpdate],
    };

    await updateDoc(orderRef, updateData);

    return {
      status: newStatus,
      updatedAt: updateData.updatedAt,
      adminNotes: updateInfo.notes,
      statusHistory: updateData.statusHistory,
    };
  } catch (error) {
    console.error("Error updating order status:", error);
    throw new Error("Failed to update order status");
  }
}

/**
 * Get client information aggregated from orders
 */
export async function getClientInfo(): Promise<ClientInfo[]> {
  try {
    const orders = await fetchAllOrders();
    const clientMap = new Map<string, ClientInfo>();

    orders.forEach((order) => {
      const email = order.clientEmail;

      if (!clientMap.has(email)) {
        clientMap.set(email, {
          email,
          name: order.clientName,
          totalOrders: 0,
          totalSpent: 0,
          orders: [],
        });
      }

      const client = clientMap.get(email)!;
      client.totalOrders++;
      client.totalSpent += order.totalAmount || 0;
      client.orders.push(order);

      // Update last order date
      if (
        !client.lastOrderDate ||
        order.createdAt.toMillis() > client.lastOrderDate.toMillis()
      ) {
        client.lastOrderDate = order.createdAt;
      }

      // Update name if we have a better one
      if (!client.name && order.clientName) {
        client.name = order.clientName;
      }
    });

    return Array.from(clientMap.values()).sort(
      (a, b) => b.totalSpent - a.totalSpent
    );
  } catch (error) {
    console.error("Error fetching client info:", error);
    throw new Error("Failed to fetch client information");
  }
}
